/**
 * layoutRequirements — what a saved scene contains, and the lowest plan that
 * could still ADD every one of those things.
 *
 * Support's question is "why can't this user edit their layout any more?". Tier
 * gates run on add/grow only (see BUILDING_TYPE_FEATURE in the web app's
 * featureGating.ts), so a layout left behind by a downgrade keeps rendering in
 * full — this names the plan its contents need today. It assumes a non-legacy
 * account: `legacy_caps` lifts the area and count caps, never the features.
 *
 * Tier data comes from tierLadder.ts, labels from sceneLabels.ts. The
 * admin-users and admin-locations edge functions duplicate `countUnits` and
 * `isAirportStructure` for their layout counts (Deno can't import admin src).
 */

import type {
  AircraftClass, ArchProfile, BuildingConfig, BuildingType, GroundZone, GroundZoneKind,
  Item, ProceduralShape, SceneGroup, StructurePresetId,
} from '@hangarmax/types'
import {
  BUILDING_TYPE_FEATURE, CLASS_TIER, FEATURE_LABELS, FEATURE_TIERS, GROUND_KIND_FEATURE,
  PILOT_BUILDING_AREA_SQFT, PRO_BUILDING_AREA_SQFT, SHAPE_FEATURE, STRUCTURE_PRESET_FEATURE,
  buildingAreaSqft, getLayoutCaps, tierRank, type Feature, type PlanSlug,
} from './tierLadder'
import {
  AIRCRAFT_CLASS_LABEL, HANGAR_TYPE_LABEL, SHAPE_LABEL, STRUCTURE_PRESET_LABEL,
} from './sceneLabels'

/** Mirrors MIN_GROUP_SIZE in apps/web/src/lib/groups/groupRules.ts. */
const MIN_GROUP_SIZE = 2

/** Loose on purpose: scene_data comes off the database unvalidated. */
export interface SceneLike {
  buildings?: Partial<BuildingConfig>[]
  items?: Partial<Item>[]
  groundZones?: Partial<GroundZone>[]
  groups?: Partial<SceneGroup>[]
}

export type CatalogClassLookup = ReadonlyMap<
  string,
  { aircraft_class?: AircraftClass | null; display_name?: string }
>

export interface BuildingSummary {
  id: string
  name: string
  type: BuildingType | null
  typeLabel: string
  widthM: number
  depthM: number
  heightM: number
  areaSqft: number
  unitCount: number
  unitOverrides: number
  archProfile: ArchProfile | null
  requiredTier: PlanSlug | null
}

export interface StructureSummary {
  id: string
  label: string
  /** `structure` = parametric preset; `shape` = a catalog-less procedural shape. */
  kind: 'structure' | 'shape'
  preset: StructurePresetId | null
  shape: ProceduralShape | null
  isAirportStructure: boolean
  requiredTier: PlanSlug | null
}

export interface GroupSummary {
  id: string
  name: string
  visible: boolean
  locked: boolean
  members: number
  /** Below MIN_GROUP_SIZE the group row is ignored by the app. */
  inert: boolean
}

export interface SceneSummary {
  buildings: BuildingSummary[]
  structures: StructureSummary[]
  groups: GroupSummary[]
  zonesByKind: Partial<Record<GroundZoneKind, number>>
  unitCount: number
  structureCount: number
  requirement: { tier: PlanSlug | null; reasons: string[] }
}

/**
 * Bays a building renders. Single-volume kinds (box, t_hangar, arch) count 0 —
 * "units" only means something for the multi-unit rows. A nested row has
 * `unit_count` units in row A and `unit_count - 1` in row B.
 */
export function countUnits(b: Partial<BuildingConfig>): number {
  const n = Number(b.units_config?.unit_count) || 0
  switch (b.type) {
    case 'single_row':
    case 'standard_t_hangar':
      return n
    case 'nested_t_hangar':
      return n > 0 ? 2 * n - 1 : 0
    default:
      return 0
  }
}

/**
 * "2 buildings · 14 units · 3 structures" from the admin-users / admin-locations
 * layout counts. Units and structures appear only when non-zero, and tolerate a
 * response from an edge function deployed before those counts existed.
 */
export function formatSceneCounts(c: { building_count: number; unit_count?: number; structure_count?: number }): string {
  const plural = (n: number, noun: string) => `${n} ${noun}${n !== 1 ? 's' : ''}`
  const parts = [plural(c.building_count, 'building')]
  if (c.unit_count) parts.push(plural(c.unit_count, 'unit'))
  if (c.structure_count) parts.push(plural(c.structure_count, 'structure'))
  return parts.join(' · ')
}

/** An airport building: a parametric preset, or a jet bridge / control tower shape. */
export function isAirportStructure(it: Partial<Item>): boolean {
  const shape = it.overrides?.procedural_shape
  return it.overrides?.structure != null || (shape != null && shape in SHAPE_FEATURE)
}

interface Need {
  tier: PlanSlug
  text: string
}

const fmt = (n: number) => Math.round(n).toLocaleString('en-US')

function featureNeed(feature: Feature | null | undefined, text: string): Need | null {
  if (!feature) return null
  const tier = FEATURE_TIERS[feature]
  return tier === 'pilot' ? null : { tier, text }
}

function areaNeed(sqft: number, what: string): Need | null {
  if (sqft > PRO_BUILDING_AREA_SQFT) {
    return { tier: 'business', text: `${what} ${fmt(sqft)} sq ft > Pro ${fmt(PRO_BUILDING_AREA_SQFT)}` }
  }
  if (sqft > PILOT_BUILDING_AREA_SQFT) {
    return { tier: 'pro', text: `${what} ${fmt(sqft)} sq ft > Pilot ${fmt(PILOT_BUILDING_AREA_SQFT)}` }
  }
  return null
}

function countNeed(count: number, kind: 'aircraft' | 'hangars'): Need | null {
  const pro = getLayoutCaps('pro', false)[kind]
  const pilot = getLayoutCaps('pilot', false)[kind]
  if (count > pro) return { tier: 'business', text: `${count} ${kind} > Pro ${pro}` }
  if (count > pilot) return { tier: 'pro', text: `${count} ${kind} > Pilot ${pilot}` }
  return null
}

function maxTier(needs: Array<Need | null>): PlanSlug | null {
  let best: PlanSlug | null = null
  for (const n of needs) {
    if (n && (best === null || tierRank(n.tier) > tierRank(best))) best = n.tier
  }
  return best
}

export function summarizeScene(
  scene: SceneLike | null | undefined,
  catalog?: CatalogClassLookup,
): SceneSummary {
  const needs: Need[] = []
  const collect = (own: Array<Need | null>) => {
    for (const n of own) if (n) needs.push(n)
    return maxTier(own)
  }

  const rawBuildings = scene?.buildings ?? []
  const buildings: BuildingSummary[] = rawBuildings.map((b, i) => {
    const type = b.type && b.type in HANGAR_TYPE_LABEL ? b.type : null
    const typeLabel = type ? HANGAR_TYPE_LABEL[type] : 'Hangar'
    const name = b.name?.trim() || typeLabel
    const widthM = Number(b.width_m) || 0
    const depthM = Number(b.depth_m) || 0
    const areaSqft = buildingAreaSqft(widthM, depthM)
    return {
      id: b.id ?? `building-${i}`,
      name,
      type,
      typeLabel,
      widthM,
      depthM,
      heightM: Number(b.height_m) || 0,
      areaSqft,
      unitCount: countUnits(b),
      unitOverrides:
        (b.units_config?.unit_overrides?.length ?? 0) + (b.units_config?.row_b_overrides?.length ?? 0),
      archProfile: type === 'arch_hangar' ? b.arch_config?.profile ?? null : null,
      requiredTier: collect([
        featureNeed(type ? BUILDING_TYPE_FEATURE[type] : null, typeLabel),
        areaNeed(areaSqft, name),
      ]),
    }
  })

  const items = scene?.items ?? []
  const structures: StructureSummary[] = []
  let aircraftCount = 0
  for (const [i, it] of items.entries()) {
    if (it.type === 'aircraft') {
      aircraftCount++
      const row = it.catalog_id ? catalog?.get(it.catalog_id) : undefined
      const cls = row?.aircraft_class
      if (cls && cls in CLASS_TIER && CLASS_TIER[cls] !== 'pilot') {
        needs.push({ tier: CLASS_TIER[cls], text: `${row?.display_name ?? 'Aircraft'} (${AIRCRAFT_CLASS_LABEL[cls]})` })
      }
      continue
    }

    const preset = it.overrides?.structure?.preset ?? null
    const shape = it.overrides?.procedural_shape ?? null
    // Catalog-less shapes only — a catalog row's procedural_shape lives on the row, not the item.
    const isShape = !preset && shape != null && !it.catalog_id && !it.overrides?.annotation_type
    if (!preset && !isShape) continue

    const airport = isAirportStructure(it)
    const label = it.label?.trim()
      || (preset ? STRUCTURE_PRESET_LABEL[preset] : undefined)
      || (shape ? SHAPE_LABEL[shape] : undefined)
      || 'Shape'
    const feature = preset ? STRUCTURE_PRESET_FEATURE[preset] : shape ? SHAPE_FEATURE[shape] : null
    structures.push({
      id: it.id ?? `item-${i}`,
      label,
      kind: preset ? 'structure' : 'shape',
      preset,
      shape: preset ? null : shape,
      isAirportStructure: airport,
      requiredTier: collect([
        featureNeed(feature, label),
        // The building-area cap covers airport structures too (commitStructurePlacement).
        airport
          ? areaNeed(buildingAreaSqft(Number(it.overrides?.width_m) || 0, Number(it.overrides?.depth_m) || 0), label)
          : null,
      ]),
    })
  }

  const count = [countNeed(aircraftCount, 'aircraft'), countNeed(rawBuildings.length, 'hangars')]
  for (const n of count) if (n) needs.push(n)

  const zones = scene?.groundZones ?? []
  const zonesByKind: Partial<Record<GroundZoneKind, number>> = {}
  for (const z of zones) {
    if (!z.kind || !(z.kind in GROUND_KIND_FEATURE)) continue
    zonesByKind[z.kind] = (zonesByKind[z.kind] ?? 0) + 1
    const feature = GROUND_KIND_FEATURE[z.kind]
    const need = featureNeed(feature, FEATURE_LABELS[feature])
    if (need) needs.push(need)
  }

  const memberCount = new Map<string, number>()
  for (const m of [...rawBuildings, ...items, ...zones]) {
    if (m.group_id) memberCount.set(m.group_id, (memberCount.get(m.group_id) ?? 0) + 1)
  }
  const groups: GroupSummary[] = (scene?.groups ?? []).map((g, i) => {
    const members = memberCount.get(g.id ?? '') ?? 0
    return {
      id: g.id ?? `group-${i}`,
      name: g.name?.trim() || 'Group',
      visible: g.visible !== false,
      locked: g.locked === true,
      members,
      inert: members < MIN_GROUP_SIZE,
    }
  })

  // Reasons at the deciding tier only, deduplicated ("T-Hangar Row ×2").
  const tier = maxTier(needs)
  const byText = new Map<string, number>()
  for (const n of needs) if (n.tier === tier) byText.set(n.text, (byText.get(n.text) ?? 0) + 1)
  const reasons = Array.from(byText, ([text, n]) => (n > 1 ? `${text} ×${n}` : text))

  return {
    buildings,
    structures,
    groups,
    zonesByKind,
    unitCount: buildings.reduce((sum, b) => sum + b.unitCount, 0),
    structureCount: structures.filter((s) => s.isAirportStructure).length,
    requirement: { tier, reasons },
  }
}
